<!--
 * 文件名：LabelAssignDialog.vue
 * 描述：数据标注任务包-选择执行人员弹框（v26.3.3 N7 行级化多行版）。
 *       一包可含多条标注明细行（taskContentType=SJBZ），逐行选执行人员：
 *       顶部行选择器（数据集名+量）切换，每行独立的已选人员+计划量+下发时间；
 *       候选人员列表按各行协配的标注人员要求预过滤（N7 行级：各行独立拉取）。
 *       2026-09-16 晚口径反转（流程不改，先分解后微调）：受理入口统一为【受理，并任务分解】
 *       → 本弹框选人 → 确认即触发拆分（emit labelAssignGroups 给父组件组包提交）；
 *       早间"行内预配+确认只回存页面内存"方案已撤销。
 * 契约：候选列表接口 POST /label-task/assign-candidates（仅执行人候选）——N7 行级入参
 *   {taskDetailGuid, tenantGuid}：后端按该行 labelConfig（科室/身份/职称）过滤候选，各行
 *   人员要求独立，故候选按当前行请求、行切换时重拉（包级 taskMainGuid 契约已随 N7 废弃）；
 *   tenantGuid=候选池租户=委托方租户（2026-09-16 拍板：执行人员取委托方认证人员）。
 *   提交链由父组件组装 POST /task/data/split { labelAssignGroups, taskDetailList(普通行子集) }。
-->
<template>
  <el-dialog title="选择执行人员" :visible.sync="dialogVisible" width="1100px" top="4vh" :close-on-click-modal="false" append-to-body>
    <div class="assign-wrap" v-if="labelRows.length">
      <!-- 顶部：标注明细行切换（每行独立选人） -->
      <div class="assign-rows">
        <span class="assign-rows-label">标注行：</span>
        <el-radio-group v-model="activeIndex" size="mini" @change="onRowSwitch">
          <el-radio-button v-for="(row, idx) in labelRows" :key="row.taskDetailGuid" :label="idx">
            {{ row.datasetName }}{{ row.dataAmount != null && row.dataAmount !== '' ? `（${row.dataAmount}${row.dataUnit || ''}）` : '' }}
          </el-radio-button>
        </el-radio-group>
      </div>
      <div class="assign-body">
        <!-- 左：候选人员（按协配人员要求预过滤，全行共用） -->
        <div class="assign-left">
          <div class="assign-panel-title">候选人员（已按标注人员要求过滤）</div>
          <el-table ref="candidateTable" :data="candidateList" v-loading="loading" border height="400px" size="mini"
            @selection-change="onSelectCandidate">
            <el-table-column type="selection" width="42"></el-table-column>
            <el-table-column prop="staffName" label="姓名" width="90" show-overflow-tooltip></el-table-column>
            <el-table-column prop="deptName" label="科室" min-width="110" show-overflow-tooltip></el-table-column>
            <el-table-column label="身份" width="70">
              <template slot-scope="scope">{{ identityText(scope.row.identity) }}</template>
            </el-table-column>
            <el-table-column prop="titleName" label="职称" width="90" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.titleName || '-' }}</template>
            </el-table-column>
            <el-table-column prop="experience" label="标注经验" min-width="110" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.experience || '-' }}</template>
            </el-table-column>
            <el-table-column label="认证状态" width="80" align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.isCertAudit == 'Y' && scope.row.approveState == 'Y'" class="cert-ok">已认证</span>
                <span v-else-if="scope.row.isCertAudit == 'Y'" class="cert-no">未认证</span>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="assign-divider"></div>
        <!-- 右：当前行已选人员 + 计划量 + 下发时间 -->
        <div class="assign-right">
          <div class="assign-panel-title">
            已选人员（{{ activeRow.datasetName }}：{{ currentSelected.length }} 人）
          </div>
          <el-table :data="currentSelected" border height="400px" size="mini">
            <el-table-column prop="staffName" label="姓名" width="90" show-overflow-tooltip></el-table-column>
            <el-table-column prop="deptName" label="科室" min-width="100" show-overflow-tooltip></el-table-column>
            <el-table-column label="计划量" width="130">
              <template slot-scope="scope">
                <el-input v-model="scope.row.planAmount" size="mini" style="width: 100px" placeholder="固定量"
                  onkeyup="value=value.replace(/[^\d]/g,'')"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="下发时间" width="150">
              <template slot-scope="scope">
                <el-date-picker v-model="scope.row.assignTime" type="date" size="mini" style="width: 130px"
                  value-format="yyyy-MM-dd" placeholder="选择日期" :clearable="false"
                  :picker-options="pickerOptions"></el-date-picker>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="60" align="center">
              <template slot-scope="scope">
                <el-button type="text" class="danger" size="mini" @click="removeSelected(scope.$index)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="assign-sum" v-if="currentSelected.length">
            本行计划量合计：{{ planSum }}<span v-if="activeRow.dataAmount != null && activeRow.dataAmount !== ''">
              / 数据量 {{ activeRow.dataAmount }}{{ activeRow.dataUnit || '' }}</span>
          </div>
        </div>
      </div>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button size="small" plain @click="dialogVisible = false">取 消</el-button>
      <el-button type="info" size="small" @click="confirm">确认下发</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "LabelAssignDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** 标注明细行列表（N7 行级化必传）：[{taskDetailGuid, datasetName, dataAmount, dataUnit}] */
    labelRows: {
      type: Array,
      default: () => [],
    },
    /** 候选池租户=委托方租户（2026-09-16 拍板：执行人员取委托方认证人员，不是受理的服务商；空串兜底登录租户） */
    tenantGuid: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      candidateList: [],
      rowSelected: [], //每行独立的已选人员：index 对齐 labelRows
      activeIndex: 0,
      replayingSelection: false, //程序化回放勾选中（屏蔽 selection-change 人工差集逻辑）
      // 下发时间不早于今天
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 24 * 60 * 60 * 1000;
        },
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
    activeRow() {
      return this.labelRows[this.activeIndex] || {};
    },
    currentSelected: {
      get() {
        return this.rowSelected[this.activeIndex] || [];
      },
      set(val) {
        this.$set(this.rowSelected, this.activeIndex, val);
      },
    },
    planSum() {
      return this.currentSelected.reduce((sum, item) => sum + (+item.planAmount || 0), 0);
    },
  },
  watch: {
    visible(val) {
      if (val) {
        if (!this.labelRows.length) {
          this.$message.error("未获取到标注明细行，请刷新后重试");
          this.dialogVisible = false;
          return;
        }
        this.activeIndex = 0;
        this.rowSelected = this.labelRows.map(() => []);
        this.loadCandidates();
      }
    },
  },
  methods: {
    identityText(value) {
      const hit = { "1": "医生", "2": "护士", "3": "药师", "4": "技师", "5": "医学生", "6": "其他" }[value];
      return hit || "-";
    },
    // 候选按当前行请求（N7 行级契约：后端按该行 labelConfig 的科室/身份/职称过滤）；
    // 快速切行时丢弃过期响应，防慢请求把别行候选盖到当前行
    loadCandidates() {
      const row = this.activeRow;
      if (!row || !row.taskDetailGuid) return;
      const reqGuid = row.taskDetailGuid;
      this.loading = true;
      this.candidateList = [];
      this.api
        .postJson(`/${serverConfig.msSalesPerformService}/label-task/assign-candidates`, {
          taskDetailGuid: reqGuid,
          tenantGuid: this.tenantGuid || localStorage.guid,
        })
        .then((res) => {
          if ((this.activeRow || {}).taskDetailGuid !== reqGuid) return; //已切行，响应过期
          this.loading = false;
          if (res.data.code == serverConfig.REQUIRE_SUCCESS_CODE) {
            this.candidateList = res.data.data || [];
            // 09-16 晚拍板"先分解后微调"：候选到达即自动分配预填（老逻辑=自动分解+事后微调的形态对齐），
            // 操作员过目/改量/删人后确认下发；两道闸（人工确认+后端 split 权威校验）都在，纯前端预填安全
            this.autoAssignCurrentRow();
            this.replaySelection();
          } else {
            this.$message.error(res.data.msg || "候选人员查询失败");
          }
        })
        .catch(() => {
          if ((this.activeRow || {}).taskDetailGuid !== reqGuid) return;
          this.loading = false;
        });
    },
    /**
     * 自动分配预填（09-16 晚拍板"按之前的逻辑：自动分配并勾选好"）：
     * 自动勾选绿态候选（approveState=Y 已认证通过；红态留给人工甄别），计划量按数据量均摊
     * （整数除，余数给第一人），下发时间默认今天。仅当该行尚未选人时执行（行切换重拉/失败重试
     * 场景不重复注入，已有人工改动则完全不动）。
     */
    autoAssignCurrentRow() {
      const row = this.activeRow;
      if (!row || (this.rowSelected[this.activeIndex] || []).length) return;
      const qualified = this.candidateList.filter((item) => item.approveState === "Y");
      if (!qualified.length) return;
      const today = this.$moment().format("YYYY-MM-DD");
      const total = row.dataAmount != null && row.dataAmount !== "" && !isNaN(+row.dataAmount) ? +row.dataAmount : null;
      // 人数多于数据量时只自动选"够数"的人（每人1例），避免预填出 0 计划量撞正整数校验
      const picked = total != null && total >= 1 && total < qualified.length ? qualified.slice(0, total) : qualified;
      const n = picked.length;
      const base = total != null ? Math.floor(total / n) : null;
      const remainder = total != null ? total - base * n : 0;
      const selected = picked.map((item, idx) => ({
        staffGuid: item.staffGuid,
        staffName: item.staffName,
        deptGuid: item.deptGuid || "",
        deptName: item.deptName || "",
        planAmount: total != null ? (idx === 0 ? base + remainder : base) : "",
        assignTime: today,
      }));
      this.$set(this.rowSelected, this.activeIndex, selected);
      this.replaySelection();
    },
    /** 行切换：重拉该行候选（各行人员要求独立，候选集可能不同） */
    onRowSwitch() {
      this.loadCandidates();
    },
    /** 回放当前行已选人员在候选表的勾选态 */
    replaySelection() {
      this.$nextTick(() => {
        if (!this.$refs.candidateTable) return;
        // 程序化 toggleRowSelection 会同步触发 selection-change，若不屏蔽，
        // onSelectCandidate 的差集过滤会在勾选中途把"还没轮到的"人从已选列表剔除，
        // 后续循环读到的 currentSelected 已缺人→勾成 false（09-16 实测 3 人只勾中第 1 个的根因）
        this.replayingSelection = true;
        const snapshot = this.currentSelected.slice();
        this.candidateList.forEach((row) => {
          const selected = snapshot.some((item) => item.staffGuid === row.staffGuid);
          this.$refs.candidateTable.toggleRowSelection(row, selected);
        });
        this.$nextTick(() => {
          this.replayingSelection = false;
        });
      });
    },
    onSelectCandidate(rows) {
      // 程序化回放产生的 selection-change 不走人工勾选差集逻辑（否则剔除未回放成员）
      if (this.replayingSelection) return;
      // 左侧勾选即时同步到当前行（已在的不重复加）
      const today = this.$moment().format("YYYY-MM-DD");
      const list = this.currentSelected.slice();
      rows.forEach((row) => {
        if (!list.some((item) => item.staffGuid === row.staffGuid)) {
          list.push({
            staffGuid: row.staffGuid,
            staffName: row.staffName,
            deptGuid: row.deptGuid || "",
            deptName: row.deptName,
            planAmount: "",
            assignTime: today,
          });
        }
      });
      // 取消勾选的从当前行移除
      this.currentSelected = list.filter(
        (item) => !this.candidateList.length || rows.some((row) => row.staffGuid === item.staffGuid)
      );
    },
    removeSelected(index) {
      const removed = this.currentSelected[index];
      this.currentSelected.splice(index, 1);
      // 同步取消左侧勾选，避免后续再勾他人时把已移除人员静默加回
      if (removed && this.$refs.candidateTable) {
        const row = this.candidateList.find((item) => item.staffGuid === removed.staffGuid);
        if (row) {
          this.$refs.candidateTable.toggleRowSelection(row, false);
        }
      }
    },
    confirm() {
      let groups;
      try {
        groups = this.labelRows.map((row, idx) => {
          const items = this.rowSelected[idx] || [];
          // 直拆形态（09-16 晚口径）：确认即拆分，每行必须已选执行人员
          if (!items.length) {
            this.activeIndex = idx;
            this.$message.error(`【${row.datasetName}】行请选择执行人员`);
            throw new Error("empty");
          }
          for (const item of items) {
            // Number.isInteger 兜底粘贴输入的小数（onkeyup 清理不拦粘贴）
            if (!Number.isInteger(+item.planAmount) || +item.planAmount <= 0) {
              this.activeIndex = idx;
              this.$message.error(`【${row.datasetName}】行请填写【${item.staffName}】的计划量（正整数）`);
              throw new Error("planAmount");
            }
            if (!item.assignTime) {
              this.activeIndex = idx;
              this.$message.error(`【${row.datasetName}】行请选择【${item.staffName}】的下发时间`);
              throw new Error("assignTime");
            }
          }
          return { taskDetailGuid: row.taskDetailGuid, items: JSON.parse(JSON.stringify(items)) };
        });
      } catch (e) {
        return; //校验失败已提示，留在弹框内修正
      }
      this.$emit("confirm", groups);
      this.dialogVisible = false;
    },
  },
};
</script>

<style scoped>
.assign-wrap {
  text-align: left; /* 抵御全局 #app text-align:center 波及 */
}
.assign-rows {
  margin-bottom: 10px;
}
.assign-rows-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-right: 8px;
}
.assign-body {
  display: flex;
}
.assign-left {
  flex: 1;
  min-width: 0;
}
.assign-divider {
  width: 12px;
  flex-shrink: 0;
}
.assign-right {
  flex: 1;
  min-width: 0;
}
.assign-panel-title {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}
.cert-ok {
  color: #67c23a;
}
.cert-no {
  color: #f56c6c;
}
.assign-sum {
  margin-top: 8px;
  font-size: 13px;
  color: #606266;
  text-align: right;
}
.danger {
  color: #f56c6c;
}
</style>
